import React from 'react'
import "../Styles/PrivacyPolicy.css";

function PrivacyPolicy() {
    return (
        <div className="privacy-policy-container">
            <div className="privacy-policy-header">
                <h1>Privacy Policy</h1>
                <p className="privacy-policy-updated">Last Updated: March 6, 2025</p>
            </div>

            <div className="privacy-policy-section">
                <h2>1. Introduction</h2>
                <p>At UniUMS ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website at <a href="https://uniums.com">https://uniums.com</a>, our home portal at <a href="https://home.uniums.com">https://home.uniums.com</a>, and our mobile applications (collectively, the "Service").</p>
                <p>Please read this Privacy Policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the Service.</p>
            </div>

            <div className="privacy-policy-section">
                <h2>2. Information We Collect</h2>

                <div className="privacy-policy-subsection">
                    <h3>2.1 Personal Information</h3>
                    <p>We may collect personal information that you voluntarily provide to us when you register for the Service, express interest in obtaining information about us or our products, or otherwise contact us. The personal information we collect may include:</p>
                    <ul className="privacy-policy-list">
                        <li>Name</li>
                        <li>Email address</li>
                        <li>Phone number</li>
                        <li>Password</li>
                        <li>Educational institution information</li>
                        <li>Profile picture</li>
                        <li>User role (Administrator, Faculty, or Student)</li>
                        <li>Any other information you choose to provide</li>
                    </ul>
                </div>

                <div className="privacy-policy-subsection">
                    <h3>2.2 Documents and Content</h3>
                    <p>We collect and store documents, files, and other content that you upload, download, share, or access while using the Service. This includes educational materials, assignments, and communications between users.</p>
                </div>

                <div className="privacy-policy-subsection">
                    <h3>2.3 Usage Information</h3>
                    <p>We automatically collect certain information about your device and how you interact with the Service, including:</p>
                    <ul className="privacy-policy-list">
                        <li>IP address</li>
                        <li>Device type and operating system</li>
                        <li>Browser type and version</li>
                        <li>Time and date of access</li>
                        <li>Features or pages viewed</li>
                        <li>System activity and crashes</li>
                        <li>Other technology on the devices you use to access the Service</li>
                    </ul>
                </div>
            </div>

            <div className="privacy-policy-section">
                <h2>3. How We Use Your Information</h2>
                <p>We use the information we collect for various purposes, including to:</p>
                <ul className="privacy-policy-list">
                    <li>Provide, maintain, and improve the Service</li>
                    <li>Process and complete transactions</li>
                    <li>Manage user accounts, including authentication and authorization</li>
                    <li>Send administrative information, such as updates, security alerts, and support messages</li>
                    <li>Respond to inquiries and provide customer support</li>
                    <li>Facilitate communication between Administrators, Faculty, and Students</li>
                    <li>Monitor and analyze usage patterns and trends</li>
                    <li>Enhance the security and functionality of the Service</li>
                    <li>Personalize your experience</li>
                    <li>Comply with legal obligations</li>
                </ul>
            </div>

            <div className="privacy-policy-section">
                <h2>4. How We Share Your Information</h2>

                <div className="privacy-policy-subsection">
                    <h3>4.1 Within Your Organization</h3>
                    <p>Information shared within your educational organization depends on user roles:</p>
                    <ul className="privacy-policy-list">
                        <li>Administrators can access information about Faculty and Students within their organization</li>
                        <li>Faculty can access information about Students assigned to their courses</li>
                        <li>Students can access Faculty information for their enrolled courses</li>
                    </ul>
                </div>

                <div className="privacy-policy-subsection">
                    <h3>4.2 Service Providers</h3>
                    <p>We may share your information with third-party service providers who perform services on our behalf, such as hosting, data analysis, payment processing, customer service, and marketing assistance.</p>
                </div>

                <div className="privacy-policy-subsection">
                    <h3>4.3 Legal Requirements</h3>
                    <p>We may disclose your information where required by law or if we believe that such action is necessary to:</p>
                    <ul className="privacy-policy-list">
                        <li>Comply with a legal obligation or process</li>
                        <li>Protect and defend our rights or property</li>
                        <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
                        <li>Protect the personal safety of users of the Service or the public</li>
                    </ul>
                </div>

                <div className="privacy-policy-subsection">
                    <h3>4.4 Business Transfers</h3>
                    <p>If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email and/or a prominent notice on our website of any change in ownership or uses of your information.</p>
                </div>
            </div>

            <div className="privacy-policy-section">
                <h2>5. Data Security</h2>
                <p>We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.</p>
            </div>

            <div className="privacy-policy-section">
                <h2>6. Data Retention</h2>
                <p>We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.</p>
            </div>

            <div className="privacy-policy-section">
                <h2>7. Your Data Protection Rights</h2>
                <p>Depending on your location, you may have the following rights:</p>
                <ul className="privacy-policy-list">
                    <li><strong>Access:</strong> You have the right to request copies of your personal information.</li>
                    <li><strong>Rectification:</strong> You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</li>
                    <li><strong>Erasure:</strong> You have the right to request that we erase your personal information, under certain conditions.</li>
                    <li><strong>Restriction:</strong> You have the right to request that we restrict the processing of your personal information, under certain conditions.</li>
                    <li><strong>Data portability:</strong> You have the right to request that we transfer the data we have collected to another organization, or directly to you, under certain conditions.</li>
                </ul>
                <p>If you wish to exercise any of these rights, please contact us using the information provided in the "Contact Us" section below.</p>
            </div>

            <div className="privacy-policy-section">
                <h2>8. Children's Privacy</h2>
                <p>The Service is intended for use by educational institutions and may be used by students who are minors. If students under the age of 18 will be using the Service, their educational institution must obtain appropriate parental consent as required by applicable law before collecting any personal information from such students.</p>
                <p>Administrators are responsible for ensuring compliance with all applicable laws and regulations regarding the collection and use of information from minors.</p>
            </div>

            <div className="privacy-policy-section">
                <h2>9. Cookies and Tracking Technologies</h2>
                <p>We use cookies and similar tracking technologies to track activity on our Service and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier.</p>
                <p>You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.</p>
            </div>

            <div className="privacy-policy-section">
                <h2>10. International Data Transfers</h2>
                <p>Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those in your jurisdiction.</p>
                <p>If you are located outside of the country where our servers are located and choose to provide information to us, please note that we transfer the data, including personal information, to that country and process it there.</p>
            </div>

            <div className="privacy-policy-section">
                <h2>11. Changes to This Privacy Policy</h2>
                <p>We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.</p>
                <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
            </div>

            <div className="privacy-policy-section">
                <h2>12. Contact Us</h2>
                <p>If you have any questions about this Privacy Policy, please contact us at:</p>
                <p className="privacy-policy-contact">
                    Email: uinums24hours@gmail.com<br />
                    Email: uinumsbhabani@gmail.com<br />
                    Email: uinumsmanas@gmail.com<br />
                    Email: bhabanishankarmohanta143@gmail.com<br />
                    Phone: +91 96926 15246<br />
                    Website: <a href="https://uniums.com">https://home.uniums.com</a>
                </p>
            </div>

            <div className="privacy-policy-footer">
                <p>© 2025 UniUMS. All rights reserved.</p>
            </div>
        </div>
    )
}

export default PrivacyPolicy